import { z } from "zod";
import { ParseResult, ok, err } from "./result";
import { Registry, parseRegistry } from "./registry";
import {
  NodeRelatedListSchema,
  NodeRelatedRef,
  PlatformMap,
  SameAsSchema,
  VISIBILITY_VALUES,
  Visibility,
} from "./manifest";
import { CatalogTerms, CatalogTermsSchema } from "../legal/catalogTerms";

/**
 * Catalog format — a (possibly nested) index of teachings. A `pack` entry is a
 * leaf (a content pack: lessons + quizzes, per CONTENT-PACK-SPEC). A `catalog`
 * entry references another catalog by `url` or embeds one `inline`, which is how
 * "a catalog of catalogs" works. A legacy `registry.json` is accepted and treated
 * as a catalog of packs. See docs/011-CATALOG-MODEL.md.
 */
export const CATALOG_SPEC_VERSION = 1;

const slug = z
  .string()
  .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/, "must be a lowercase slug (a-z, 0-9, dashes)");
const semver = z.string().regex(/^\d+\.\d+\.\d+$/, "must be a semver version (e.g. 1.0.0)");
const isoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "must be an ISO date (YYYY-MM-DD)");
const langTag = z
  .string()
  .regex(/^[a-z]{2}(?:-[A-Z]{2})?$/, "must be a language tag (e.g. en, es, pt-BR)");

export interface CatalogPackEntry {
  kind: "pack";
  id: string;
  title: string;
  version?: string;
  /** Git-derived newest change date (ISO YYYY-MM-DD), for an "Updated" hint in lists. */
  updated?: string;
  icon?: string;
  manifestUrl: string;
  tags?: string[];
  access?: "free" | "paid";
  /** Languages this pack offers, for browse/filter (the manifest is authoritative). */
  languages?: string[];
  /** Whether this pack's files may be downloaded for offline use. Absent = allowed. */
  downloadable?: boolean;
  /**
   * Globally unique per-appearance id (`<parentCatalogId>/<itemId>`, materials plan 022),
   * distinct from the canonical `id` — a cross-listed pack has one `id` but a `placementId`
   * per placement. Optional; inert until a consumer (e.g. a saved reference) opts in. See
   * docs/055-STORED-REFS-CANONICAL-IDS.md. Never used to key progress/bookmarks/pins.
   */
  placementId?: string;
  /**
   * Plain-text blurb about this pack entry, shown via the ⓘ details button (docs/
   * CONTENT-DESCRIPTIONS.md). No cross-level resolution — this is the entry's own text.
   */
  description?: string;
  /**
   * Browse visibility (docs/038-CONTENT-CAPABILITY-FLAGS.md §3), resolved most-restrictive-
   * wins via `resolveVisibility` (manifest.ts). Omitted = public. `hidden`/`private`
   * pack entries are dropped from the built catalog tree (`content/catalogTree.ts`).
   */
  visibility?: Visibility;
  /**
   * Platform availability opt-out (materials plan 060; docs/207-PLATFORM-AVAILABILITY-
   * FILTER.md), resolved most-restrictive-wins via `resolvePlatforms` (manifest.ts).
   * Omitted = every platform. An entry opted out of the current platform is dropped from
   * the built catalog tree (`content/catalogTree.ts`), same filtering point as `visibility`.
   */
  platforms?: PlatformMap;
  /**
   * Node-level "Related courses" see-also (docs/242-RELATED-AND-SAMEAS-RENDERING.md v0) —
   * sibling node ids (bare, or `{id,title}` for a carried title), resolved by
   * `content/nodeRelated.ts`. Distinct from the lesson-level `related` (docs/101).
   */
  related?: NodeRelatedRef[];
  /**
   * Opaque cross-placement equality key (docs/242-RELATED-AND-SAMEAS-RENDERING.md v0):
   * entries carrying the SAME `sameAs` string are the same underlying content published
   * under different ids (e.g. a same-school cross-listed course). Never parsed/interpreted
   * — grouped by plain string equality only, see `content/sameAs.ts` `sameAsGroups`. Never
   * itself a progress/storage key.
   */
  sameAs?: string;
  /**
   * Free-form author-declared section label (docs/249-CATALOG-GROUPING-AND-SECTIONS.md
   * v0), e.g. `"300-level"` or `"Newlyweds"` — bucketed verbatim by `content/
   * catalogGrouping.ts` `groupEntries(by: "group")`. Distinct from `tags` (a filter
   * axis, many-to-many) — `group` is a single-value partition, one section per entry.
   * Cross-repo: the materials build doesn't emit this yet (tracked under
   * docs/068-MATERIALS-REPO-SCHEMA-SYNC.md); inert (no grouping control appears) until
   * it does.
   */
  group?: string;
  /**
   * Author-declared course level (docs/249-CATALOG-GROUPING-AND-SECTIONS.md v0;
   * reworked docs/268-CATALOG-GROUP-BY-AXES.md F2), e.g. `201`/`301` — banded via
   * `Math.floor(level / 100)` and labeled "Level {n}" (`n` = the banded number, e.g.
   * `201` → "Level 2") by `content/catalogGrouping.ts`. When this field is absent,
   * the same keyer falls back to parsing a `NNN` course code out of the entry's
   * `title` (e.g. `"MAR 201"` → Level 2) — a stamped value here still wins over a
   * parsed one. Also the **auto-default** group-by signal (this field only, not the
   * title-parsing fallback): a node with any `level`-carrying entry defaults its
   * "Group by" control to Level. Cross-repo: not yet emitted by the materials build
   * (docs/068); the field itself stays inert until it is, though the title-parsing
   * fallback already makes the Level chip live on today's by-course content.
   */
  level?: number;
  /**
   * Terms-of-usage this specific pack placement requires the user to accept before
   * opening (docs/297-PROVIDER-CATALOG-TERMS-ACCEPTANCE.md v0) — a "catalog-entry"-level
   * declaration, checked FETCH-FREE (from the already-loaded catalog tree, before the
   * pack's own manifest is ever fetched) via `catalogTermsScope`/`termsToAccept`
   * (`legal/catalogTerms.ts`). Distinct from `Catalog.terms` below (that catalog
   * document's own subtree-wide terms) — a user may owe both. Additive/optional
   * (docs/108-safe).
   */
  terms?: CatalogTerms;
}

export interface CatalogNodeEntry {
  kind: "catalog";
  id: string;
  title: string;
  /** Git-derived rolled-up newest change date (ISO YYYY-MM-DD) within this child. */
  updated?: string;
  icon?: string;
  /** A remote catalog to load lazily… */
  url?: string;
  /** …or an embedded catalog (recursive). Exactly one of url/inline is required. */
  inline?: Catalog;
  /**
   * Globally unique per-appearance id (`<parentCatalogId>/<itemId>`, materials plan 022),
   * distinct from the canonical `id` — a cross-listed catalog has one `id` but a
   * `placementId` per placement. Optional; see docs/055-STORED-REFS-CANONICAL-IDS.md.
   */
  placementId?: string;
  /**
   * Plain-text blurb about this catalog entry, shown via the ⓘ details button (docs/
   * CONTENT-DESCRIPTIONS.md). No cross-level resolution — this is the entry's own text.
   */
  description?: string;
  /**
   * Browse visibility of this catalog reference (docs/038-CONTENT-CAPABILITY-FLAGS.md §3).
   * Omitted = public. `hidden`/`private` child catalogs are dropped from the built
   * catalog tree (`content/catalogTree.ts`).
   */
  visibility?: Visibility;
  /**
   * Platform availability opt-out of this catalog *reference* (materials plan 060;
   * docs/207-PLATFORM-AVAILABILITY-FILTER.md) — distinct from the *referenced document's*
   * own `Catalog.platforms` below. Both apply, most-restrictive-wins (mirrored onto
   * `ResolvedCatalog.platforms`, `content/catalogLoader.ts`).
   */
  platforms?: PlatformMap;
  /**
   * Node-level "Related courses" see-also (docs/242-RELATED-AND-SAMEAS-RENDERING.md v0) —
   * see `CatalogPackEntry.related`'s doc comment (same shape/contract, one level up).
   */
  related?: NodeRelatedRef[];
  /**
   * Opaque cross-placement equality key (docs/242-RELATED-AND-SAMEAS-RENDERING.md v0) —
   * see `CatalogPackEntry.sameAs`'s doc comment (same shape/contract, one level up).
   */
  sameAs?: string;
  /**
   * Free-form author-declared section label (docs/249-CATALOG-GROUPING-AND-SECTIONS.md
   * v0) for this child catalog — see `CatalogPackEntry.group`'s doc comment (same
   * shape/contract, one level up).
   */
  group?: string;
  /**
   * Author-declared course level (docs/249-CATALOG-GROUPING-AND-SECTIONS.md v0) for
   * this child catalog — see `CatalogPackEntry.level`'s doc comment (same shape/
   * contract, one level up).
   */
  level?: number;
}

export type CatalogEntry = CatalogPackEntry | CatalogNodeEntry;

/**
 * A named filter axis the app renders as a chip-filter group over a catalog's entries
 * (materials plan 204). Reuses the lesson-level `list` module's facet vocabulary
 * (docs/175). An entry opts in either via the built-in `source: "languages"` axis
 * (matched against the entry's `languages`) or via `tags` (matched against `tags`).
 * Exactly one of `source` / `tags`. NOTE: v0 language filtering needs no facet
 * declaration — the app auto-derives language chips when a catalog spans >1 language.
 */
export interface CatalogFacet {
  id: string;
  title: string;
  source?: "languages";
  tags?: string[];
}

/**
 * Singular/plural noun pair for one count kind (docs/260 Open decision 2 — unlike the
 * app's built-in `catalog.packCount` default, which has no singular form, an override
 * DOES get a real `one`/`other` split, so `pack.one` can read "1 course").
 *
 * Each variant is a **plain string already localized by the materials build** — NOT a
 * `{en,es,pt}` map. The catalog document is fetched per-language (`loadCatalogFromUrl(u,
 * { lang })`), and the materials build (plan 338 `_localize_count_labels`) swaps each noun
 * to that catalog's language before emit, so the string is already in the right language.
 * (This corrects docs/260 v0's wrong `{en,es,pt}`-map assumption — Open decision 1 — which
 * hard-failed a catalog whose `countLabels` the build emitted as strings.) Each string
 * should contain a `{n}` placeholder, interpolated by `formatCountLabel`.
 */
export interface CountLabelPair {
  one: string;
  other: string;
}

/**
 * Overridable nouns for a catalog node's own two-count meta line (docs/
 * 260-OVERRIDABLE-COUNT-LABEL-NOUNS.md) — `catalog` renames the sub-catalog count
 * ("N catalogs" → e.g. "N schools"), `pack` renames the descendant-pack count ("N
 * packs" → e.g. "N courses"). **Parent describes children**: this lives on the
 * catalog *document* being counted (mirrors `facets`, not `group`/`level`, which
 * mirror from the referencing entry — see Findings §B of the doc). Each key is
 * independently optional (docs/260 Open decision 4) — a node may override only
 * `catalog` or only `pack`, and the other keeps the app's default noun. Missing
 * entirely, or missing the active language's variant, falls back **totally** to the
 * built-in `catalog.catalogCount.{one,other}` / `catalog.packCount` UI strings — see
 * `content/countLabels.ts` `formatCountLabel`.
 */
export interface CountLabels {
  catalog?: CountLabelPair;
  pack?: CountLabelPair;
}

export interface Catalog {
  specVersion: number;
  id: string;
  title: string;
  /** Languages represented across this catalog's packs (advisory, for filtering). */
  languages?: string[];
  /** Filter axes rendered as chip groups over this catalog's entries (plan 204). Additive. */
  facets?: CatalogFacet[];
  /**
   * Overridable nouns for this catalog document's own two-count meta line — see
   * {@link CountLabels}'s own doc comment for the model + fallback contract.
   * Additive/`specVersion`-unchanged (docs/260-OVERRIDABLE-COUNT-LABEL-NOUNS.md v0).
   */
  countLabels?: CountLabels;
  entries: CatalogEntry[];
  /**
   * Plain-text blurb about this catalog, shown via the ⓘ details button (docs/
   * CONTENT-DESCRIPTIONS.md). No cross-level resolution — a catalog page shows only its
   * own description, never a child's or parent's.
   */
  description?: string;
  /**
   * This catalog document's own browse visibility (docs/038-CONTENT-CAPABILITY-FLAGS.md §3)
   * — distinct from the *entry* (`CatalogNodeEntry.visibility`) that references it.
   * Omitted = public.
   */
  visibility?: Visibility;
  /**
   * This catalog document's own platform availability opt-out (materials plan 060;
   * docs/207-PLATFORM-AVAILABILITY-FILTER.md) — distinct from the *entry*
   * (`CatalogNodeEntry.platforms`) that references it; both apply, most-restrictive-wins.
   * Omitted = every platform.
   */
  platforms?: PlatformMap;
  /**
   * Display preference (docs/085-MANAGE-CATALOGS-PAGE.md): a publisher's opt-out of
   * surfacing this catalog's hosting **URL** in the app's "Manage catalogs" list — omitted
   * or `true` shows it (default), `false` shows the catalog's title + a muted "hidden
   * source" label instead. **Not a security control** — the app already has the URL (it
   * fetched the manifest from it); this only governs what's displayed. Cross-repo: a new
   * field the materials/content build must start emitting to opt in (tracked under
   * docs/068-MATERIALS-REPO-SCHEMA-SYNC.md); until it does, the default (show) applies.
   */
  showSourceUrl?: boolean;
  /**
   * Tombstone flag (docs/256-CATALOG-AUTO-REMOVAL.md v0; materials-repo plan 336) — a
   * retired root catalog's builder emits a minimal `catalog.json` with `retracted: true`
   * instead of deleting the file outright (a genuine 404/network error stays
   * indistinguishable from a transient outage). The app's interval poller
   * (`content/catalogPoll.ts` `pollOneCatalog`) treats this as a pure signal, auto-removing
   * the user-added catalog only when the opt-in `autoRemove` preference
   * (`content/catalogUpdatePolicy.ts`) resolves true; otherwise it's ignored and the
   * catalog simply renders as empty — the same fail-safe an app older than this field
   * shows (Zod strips the unknown key). Additive, `specVersion` unchanged.
   */
  retracted?: boolean;
  /** Optional human-readable reason accompanying {@link retracted} (e.g. "merged into the
   * 2026 curriculum") — display-only, never parsed/interpreted by the app. */
  retractedReason?: string;
  /**
   * This catalog document's own terms-of-usage (docs/297-PROVIDER-CATALOG-TERMS-
   * ACCEPTANCE.md v0 — the doc's "catalog-level" scope) — applies to this catalog's
   * whole subtree, distinct from a specific `CatalogPackEntry.terms` above and from a
   * signed-in provider's own `ProviderMetadata.terms` (`providers/providerMetadata.ts`).
   * Mirrored onto `ResolvedCatalog.terms`/`CatalogNode.terms` the same node's-own-
   * document mirror point `description`/`facets` are (never resolved across levels).
   * Additive/optional (docs/108-safe).
   */
  terms?: CatalogTerms;
}

const VisibilitySchema = z.enum(VISIBILITY_VALUES).optional();

// Local copy (mirrors `VisibilitySchema` above) so this file doesn't need to export the
// schema from manifest.ts; the `PlatformMap` *type* is shared, the Zod shape is not.
const PlatformMapSchema = z.record(z.boolean()).optional();

const CatalogPackEntrySchema = z.object({
  kind: z.literal("pack"),
  id: slug,
  title: z.string().min(1, "title is required"),
  version: semver.optional(),
  updated: isoDate.optional(),
  icon: z.string().url("icon must be a URL").optional(),
  manifestUrl: z.string().url("manifestUrl must be a URL"),
  tags: z.array(z.string()).optional(),
  access: z.enum(["free", "paid"]).optional(),
  languages: z.array(langTag).nonempty().optional(),
  downloadable: z.boolean().optional(),
  placementId: z.string().optional(),
  description: z.string().optional(),
  visibility: VisibilitySchema,
  platforms: PlatformMapSchema,
  // Cast opt-in (docs/251-MEDIA-CASTING.md), resolved most-restrictive with the pack/lesson
  // levels via `resolveCastable`. Default off.
  castable: z.boolean().optional(),
  // TV-media hint (docs/322-ROKU-FEED-COVERAGE.md v0 Part 3) — mirrors `Manifest.
  // tvMedia`'s doc comment (`core/manifest.ts`): NEGATIVE-skip opt-out, `false` means
  // "known media-less, don't fetch this pack's manifest when walking for a TV feed";
  // absent/`true` = walk it. Consumed only by the Worker's `rokuFeed.ts`; no app-side
  // resolver (unlike `castable`) — deliberately schema-only in v0.
  tvMedia: z.boolean().optional(),
  related: NodeRelatedListSchema,
  sameAs: SameAsSchema,
  group: z.string().optional(),
  level: z.number().optional(),
  terms: CatalogTermsSchema.optional(),
});

// Input `unknown` too — it embeds `CatalogSchema` (an inline sub-catalog), which is now
// input-`unknown` (see its note re: countLabels `.catch()`).
const CatalogNodeEntrySchema: z.ZodType<CatalogNodeEntry, z.ZodTypeDef, unknown> = z.lazy(() =>
  z
    .object({
      kind: z.literal("catalog"),
      id: slug,
      title: z.string().min(1, "title is required"),
      updated: isoDate.optional(),
      icon: z.string().url("icon must be a URL").optional(),
      url: z.string().url("url must be a URL").optional(),
      inline: CatalogSchema.optional(),
      placementId: z.string().optional(),
      description: z.string().optional(),
      visibility: VisibilitySchema,
      platforms: PlatformMapSchema,
      castable: z.boolean().optional(),
      // TV-media hint (docs/322-ROKU-FEED-COVERAGE.md v0 Part 3) — see
      // `CatalogPackEntrySchema.tvMedia`'s doc comment (same shape/contract, one
      // level up: NEGATIVE-skip, `false` skips recursing into this sub-catalog
      // entirely when walking for a TV feed).
      tvMedia: z.boolean().optional(),
      group: z.string().optional(),
      level: z.number().optional(),
      related: NodeRelatedListSchema,
      sameAs: SameAsSchema,
    })
    .superRefine((entry, ctx) => {
      if (!entry.url && !entry.inline) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "a catalog entry needs a url or an inline catalog",
        });
      }
    }),
);

const CatalogEntrySchema: z.ZodType<CatalogEntry, z.ZodTypeDef, unknown> = z.lazy(() =>
  z.union([CatalogPackEntrySchema, CatalogNodeEntrySchema]),
);

const CatalogFacetSchema = z
  .object({
    id: slug,
    title: z.string().min(1, "facet title is required"),
    source: z.literal("languages").optional(),
    tags: z.array(z.string()).nonempty().optional(),
  })
  .refine((f) => (f.source === undefined) !== (f.tags === undefined), {
    message: "a facet must set exactly one of `source` or `tags`",
  });

// docs/260-OVERRIDABLE-COUNT-LABEL-NOUNS.md — each noun variant is a plain string ALREADY
// localized by the materials build (plan 338; the catalog is fetched per-language), NOT a
// `{en,es,pt}` map (v0's wrong assumption, which hard-failed a catalog the build emitted
// with string values — the bug this corrects).
const CountLabelPairSchema = z.object({
  one: z.string(),
  other: z.string(),
});
const CountLabelsSchema = z.object({
  catalog: CountLabelPairSchema.optional(),
  pack: CountLabelPairSchema.optional(),
});

// Input is `unknown` (untrusted JSON) — required so an optional field can use `.catch()`
// to degrade a malformed value instead of failing the whole catalog (countLabels below).
const CatalogSchema: z.ZodType<Catalog, z.ZodTypeDef, unknown> = z.lazy(() =>
  z
    .object({
      specVersion: z.literal(CATALOG_SPEC_VERSION),
      id: slug,
      title: z.string().min(1, "title is required"),
      languages: z.array(langTag).nonempty().optional(),
      facets: z.array(CatalogFacetSchema).optional(),
      // `.catch(undefined)`: `countLabels` is an OPTIONAL display-only override, so a
      // malformed value must degrade to "use the default nouns" — NEVER fail the whole
      // catalog (docs/108-BACKWARD-COMPATIBILITY.md). v0 shipped without this and a
      // build-emitted shape mismatch made an entire catalog unloadable ("could not load
      // …: countLabels.catalog.one: Expected object, received string").
      countLabels: CountLabelsSchema.optional().catch(undefined),
      entries: z.array(CatalogEntrySchema),
      description: z.string().optional(),
      visibility: VisibilitySchema,
      platforms: PlatformMapSchema,
      castable: z.boolean().optional(),
      // TV-media hint (docs/322-ROKU-FEED-COVERAGE.md v0 Part 3) — this catalog
      // DOCUMENT's own hint (distinct from the *entry* referencing it,
      // `CatalogNodeEntrySchema.tvMedia` above; both apply). See
      // `CatalogPackEntrySchema.tvMedia`'s doc comment for the shape/contract.
      tvMedia: z.boolean().optional(),
      showSourceUrl: z.boolean().optional(),
      retracted: z.boolean().optional(),
      retractedReason: z.string().optional(),
      terms: CatalogTermsSchema.optional(),
    })
    .superRefine((catalog, ctx) => {
      const seen = new Set<string>();
      catalog.entries.forEach((entry, i) => {
        if (seen.has(entry.id)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: `duplicate entry id: "${entry.id}"`,
            path: ["entries", i, "id"],
          });
        }
        seen.add(entry.id);
      });
    }),
);

function formatIssue(issue: z.ZodIssue): string {
  const path = issue.path.join(".");
  return path ? `${path}: ${issue.message}` : issue.message;
}

/** A legacy registry ({ name, packs: [...] }) viewed as a catalog of packs. */
function registryToCatalog(registry: Registry): Catalog {
  return {
    specVersion: CATALOG_SPEC_VERSION,
    id: "registry", // legacy registries carry a name, not an id
    title: registry.name,
    entries: registry.packs.map((p) => ({
      kind: "pack" as const,
      id: p.id,
      title: p.title,
      version: p.version,
      icon: p.icon,
      manifestUrl: p.manifestUrl,
      tags: p.tags,
      access: p.access,
    })),
  };
}

export function parseCatalog(input: unknown): ParseResult<Catalog> {
  // Version gate: a newer spec means "update the app", not a validation error.
  if (input && typeof input === "object" && "specVersion" in input) {
    const sv = (input as { specVersion: unknown }).specVersion;
    if (typeof sv === "number" && sv > CATALOG_SPEC_VERSION) {
      return err([
        `This catalog requires a newer app version (specVersion ${sv}; this app supports ${CATALOG_SPEC_VERSION}). Please update the app.`,
      ]);
    }
  }

  // Backward compatibility: accept a legacy registry as a catalog of packs.
  if (input && typeof input === "object" && "packs" in input && !("entries" in input)) {
    const reg = parseRegistry(input);
    if (!reg.ok) return reg;
    return ok(registryToCatalog(reg.value));
  }

  // Friendly guidance when someone hands us a *pack manifest* (has `lessons`) where a
  // *catalog* (needs `entries`) is expected — the common "entries: required" confusion.
  // A catalog is an index of packs; a pack manifest is one pack's lessons. See docs/011.
  if (isPackManifestShape(input)) {
    return err([
      'This looks like a pack manifest (it lists "lessons"), not a catalog (which lists packs ' +
        'under "entries"). To add a single pack, add it by its manifest URL, or wrap it in a ' +
        'catalog that references it under "entries".',
    ]);
  }

  const result = CatalogSchema.safeParse(input);
  if (result.success) return ok(result.data);
  return err(result.error.issues.map(formatIssue));
}

/**
 * Whether a parsed JSON value looks like a **pack manifest** rather than a **catalog** —
 * it lists `lessons` and has no `entries`. Used to give a friendly error (above) and to
 * auto-wrap a pack manifest fetched from a URL into a one-pack catalog
 * (`packManifestToCatalog`), so "add a pack by its manifest URL" works. See docs/011.
 */
export function isPackManifestShape(input: unknown): boolean {
  return (
    !!input && typeof input === "object" && "lessons" in input && !("entries" in input)
  );
}

/**
 * Wrap a **pack manifest** into a synthetic **one-pack catalog** that references it by
 * `manifestUrl`, so a user can add a single pack by its manifest URL and it flows through
 * the normal catalog → pack → lesson pipeline. Returns `null` when `input` isn't a pack
 * manifest with the string `id`/`title` a catalog entry needs. Pure — the caller (the URL
 * loader) supplies the real `manifestUrl`; the pack's lesson files resolve relative to it.
 */
export function packManifestToCatalog(input: unknown, manifestUrl: string): Catalog | null {
  if (!isPackManifestShape(input)) return null;
  const m = input as { id?: unknown; title?: unknown };
  if (typeof m.id !== "string" || typeof m.title !== "string" || !m.id || !m.title) return null;
  return {
    specVersion: CATALOG_SPEC_VERSION,
    // A distinct id from the pack's so a stored reference never collides pack-id with
    // catalog-id; `pack-<slug>` stays a valid slug.
    id: `pack-${m.id}`,
    title: m.title,
    entries: [{ kind: "pack", id: m.id, title: m.title, manifestUrl }],
  };
}

export function parseCatalogJson(text: string): ParseResult<Catalog> {
  let data: unknown;
  try {
    data = JSON.parse(text);
  } catch {
    return err(["catalog is not valid JSON"]);
  }
  return parseCatalog(data);
}
